import NumberFormat from 'react-number-format';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  FormControlLabel,
  Grid,
  TextField,
} from '@mui/material';

import { NomenclatureGroup } from '@/types';
import { useCreateNomenclatureGroupMutation } from '@/services';
import { messages } from '@/utils/helpers';
import { NomenclatureGroups } from '@/utils/enums';

export interface CreateNomenclatureGroupProps extends DialogProps {
  onClose(): void;
}

const CreateNomenclatureGroup: React.FC<CreateNomenclatureGroupProps> = ({ onClose, ...props }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [createNomenclatureGroup, createNomenclatureGroupResponse] = useCreateNomenclatureGroupMutation();

  const { values, errors, touched, setFieldValue, handleSubmit, handleChange } = useFormik<NomenclatureGroup>({
    initialValues: {
      collateral_rate_percent: 0,
      gt_30_days: 0,
      lt_30_days: 0,
      name: NomenclatureGroups.JEVELRY,
      is_price_editable: false,
    },
    validationSchema: yup.object().shape({
      collateral_rate_percent: yup.number().required(messages.form.REQUIRED),
      gt_30_days: yup.number().required(messages.form.REQUIRED),
      lt_30_days: yup.number().required(messages.form.REQUIRED),
      name: yup.string().required(messages.form.REQUIRED),
      is_price_editable: yup.boolean().required(messages.form.REQUIRED),
    }),
    async onSubmit(nomenclatureGroup, { resetForm }) {
      try {
        await createNomenclatureGroup(nomenclatureGroup).unwrap();

        onClose();
        resetForm();
      } catch (error) {
        enqueueSnackbar(error.data, { variant: 'error' });
      }
    },
  });

  return (
    <Dialog fullWidth {...props}>
      <DialogTitle fontWeight="bold">Добавить номенклатурную группу</DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item lg={6} md={6} xs={12}>
              <TextField
                name="name"
                label="Наименование"
                placeholder="Введите значение"
                value={values.name}
                onChange={handleChange}
                disabled={createNomenclatureGroupResponse.isLoading}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name ? errors.name : ''}
                fullWidth
              />
            </Grid>

            <Grid item lg={6} md={6} xs={12}>
              <NumberFormat
                customInput={TextField}
                name="collateral_rate_percent"
                label="Процентная ставка залога"
                placeholder="Введите значение"
                value={values.collateral_rate_percent}
                onChange={handleChange}
                disabled={createNomenclatureGroupResponse.isLoading}
                error={touched.collateral_rate_percent && Boolean(errors.collateral_rate_percent)}
                helperText={touched.collateral_rate_percent ? errors.collateral_rate_percent : ''}
                fullWidth
              />
            </Grid>

            <Grid item lg={6} md={6} xs={12}>
              <NumberFormat
                customInput={TextField}
                name="lt_30_days"
                label="Общая пени (<= 30)"
                placeholder="Введите значение"
                value={values.lt_30_days}
                onChange={handleChange}
                disabled={createNomenclatureGroupResponse.isLoading}
                error={touched.lt_30_days && Boolean(errors.lt_30_days)}
                helperText={touched.lt_30_days ? errors.lt_30_days : ''}
                fullWidth
              />
            </Grid>

            <Grid item lg={6} md={6} xs={12}>
              <NumberFormat
                customInput={TextField}
                name="gt_30_days"
                label="Общая пени (>= 30)"
                placeholder="Введите значение"
                value={values.gt_30_days}
                onChange={handleChange}
                disabled={createNomenclatureGroupResponse.isLoading}
                error={touched.gt_30_days && Boolean(errors.gt_30_days)}
                helperText={touched.gt_30_days ? errors.gt_30_days : ''}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                label="Редактировать начальную стоимость"
                control={
                  <Checkbox
                    name="is_price_editable"
                    value={values.is_price_editable}
                    onChange={(event, checked) => setFieldValue('is_price_editable', checked)}
                  />
                }
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            type="button"
            color="secondary"
            variant="outlined"
            disabled={createNomenclatureGroupResponse.isLoading}
            onClick={onClose}
          >
            Закрыть
          </Button>

          <Button type="submit" variant="contained" disabled={createNomenclatureGroupResponse.isLoading}>
            Добавить
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateNomenclatureGroup;
